import { useCallback, useRef } from 'react'

import {
  SavingIndicator,
  SavingIndicatorState,
} from '@/shared/ui/saving-indicator'

import { useUpdateRundownCue } from '@/entities/rundown'
import type { UpdateRundownCuePayload } from '@/entities/rundown'

import { useCueId } from './cue.context'
import { useCue } from './cue.hooks'
import { CueSubtitle } from './CueSubtitle'
import { CueTitle } from './CueTitle'

type CueHeaderProps = {
  rundownId?: string
  readonly?: boolean
}

export function CueHeader({ rundownId, readonly = false }: CueHeaderProps) {
  const cueId = useCueId()
  const cue = useCue(cueId)

  const {
    mutate: updateRundownCue,
    isPending,
    isSuccess,
    isError,
  } = useUpdateRundownCue()

  // Refs for managing focus and key handling on the title/subtitle ContentEditable
  const titleRef = useRef<HTMLDivElement>(null)
  const subtitleRef = useRef<HTMLDivElement>(null)

  // Wrapper function to handle save states and errors
  const handleUpdateCue = useCallback(
    (updates: UpdateRundownCuePayload) => {
      if (!rundownId) return

      updateRundownCue({
        rundownId,
        cueId,
        payload: updates,
        params: {},
      })
    },
    [cueId, rundownId, updateRundownCue],
  )

  if (!cue) return null

  return (
    <div className="h-full flex relative">
      <div className="sticky top-[107px] px-3 pt-8 pb-2 flex flex-col w-full h-min">
        <CueTitle
          inputRef={titleRef}
          subtitleRef={subtitleRef}
          value={cue.title}
          onChange={(title) => handleUpdateCue({ title })}
          disabled={readonly || cue.settings?.preventEdits}
          readonly={readonly}
        />
        <CueSubtitle
          inputRef={subtitleRef}
          titleRef={titleRef}
          className="text-sm text-white/60"
          value={cue.subtitle}
          onChange={(subtitle) => handleUpdateCue({ subtitle })}
          disabled={readonly || cue.settings?.preventEdits}
          readonly={readonly}
        />
      </div>

      <SavingIndicator
        states={{
          [SavingIndicatorState.SAVING]: isPending,
          [SavingIndicatorState.ERROR]: isError,
          [SavingIndicatorState.SAVED]: isSuccess,
        }}
        className="absolute z-30 bottom-1 right-1"
      />
    </div>
  )
}
